import React, { useCallback, useEffect, useState, useRef } from 'react';
import { getTenant } from '../../../theme/tokens';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon, UploadIcon, FileTextIcon, XIcon, CheckCircleIcon, LoaderIcon } from 'lucide-react';
import { SearchableSelect } from '../../../shared/ui/SearchableSelect';
import { mockSuppliers } from '../../suppliers/mocks/mockSuppliers';
import { mockBills } from '../mocks/mockBills';
const CURRENCIES = [getTenant().currency.code, 'USD', 'EUR', 'GBP', 'SGD'];
interface UploadedFile {
  name: string;
  size: number;
  type: string;
}
interface ExtractedData {
  invoiceNumber: string;
  localId: string;
  dueDate: string;
  amountDue: string;
  supplierName: string;
}
export function BillForm() {
  const {
    t
  } = useTranslation('bills');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const billId = searchParams.get('id');
  const prefillSupplierId = searchParams.get('supplierId');
  const isEditMode = !!billId;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editingBill = isEditMode ? mockBills.find(b => b.id === billId) || null : null;
  const [uploadedFile, setUploadedFile] = useState<UploadedFile | null>(null);
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractionDone, setExtractionDone] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [formData, setFormData] = useState({
    invoiceNumber: editingBill?.billNumber || '',
    supplierName: editingBill?.supplierName || '',
    localId: '',
    dueDate: editingBill?.dueDate || '',
    amountDue: editingBill?.amount?.toString() || '',
    currency: editingBill?.currency || getTenant().currency.code
  });
  // Pre-fill supplier from query param
  useEffect(() => {
    if (prefillSupplierId && !isEditMode) {
      const supplier = mockSuppliers.find(s => s.id === prefillSupplierId);
      if (supplier) {
        setFormData(prev => ({
          ...prev,
          supplierName: supplier.name
        }));
      }
    }
  }, [prefillSupplierId, isEditMode]);
  const supplierOptions = mockSuppliers.map(s => s.name);
  // Simulate OCR extraction from uploaded file
  const simulateExtraction = useCallback((fileName: string) => {
    setIsExtracting(true);
    setExtractionDone(false);
    // Simulate a delay for "processing"
    setTimeout(() => {
      // Mock extracted data based on file name patterns
      const mockExtracted: ExtractedData = {
        invoiceNumber: `INV-${Date.now().toString().slice(-6)}`,
        localId: `LOC-${Math.floor(Math.random() * 90000 + 10000)}`,
        dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        amountDue: '',
        supplierName: ''
      };
      setFormData(prev => ({
        ...prev,
        invoiceNumber: prev.invoiceNumber || mockExtracted.invoiceNumber,
        localId: prev.localId || mockExtracted.localId,
        dueDate: prev.dueDate || mockExtracted.dueDate
      }));
      setIsExtracting(false);
      setExtractionDone(true);
    }, 1800);
  }, []);
  const handleFileSelect = (file: File) => {
    const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!validTypes.includes(file.type)) return;
    setUploadedFile({
      name: file.name,
      size: file.size,
      type: file.type
    });
    simulateExtraction(file.name);
  };
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  }, [simulateExtraction]);
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };
  const handleDragLeave = () => setIsDragOver(false);
  const removeFile = () => {
    setUploadedFile(null);
    setExtractionDone(false);
    setIsExtracting(false);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    navigate('/bills');
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };
  return <div className="max-w-xl mx-auto pb-12" data-id="element-4114" data-mp="26elz2ioxah9v">
      {/* Header */}
      <div className="mb-6" data-id="element-4115" data-mp="2kf6mjs5s919p">
        <button onClick={() => navigate('/bills')} className="flex items-center text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-4116" data-mp="2keeq6f4d2zid">
          <ArrowLeftIcon size={16} className="mr-2" data-id="element-4117" data-mp="q1otxqqite7t" />
          {t('pages.BillApprovalWizard.back_to_bills')}
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-4118" data-mp="l45ggt1nd5gx">
        <div className="px-8 py-6 border-b border-gray-100" data-id="element-4119" data-mp="2wr8xy1idhu75">
          <h1 className="text-xl font-semibold text-gray-900" data-id="element-4120" data-mp="31n7kdjk5524x">
            {isEditMode ? 'Edit bill' : 'Add bill'}
          </h1>
          <p className="text-sm text-gray-500 mt-1" data-id="element-4121" data-mp="2mz2yy7tpecig">
            {t('pages.BillForm.upload_your_invoice_and_well_extract')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-6" data-id="element-4122" data-mp="2dyet40egrign">
          {/* File Upload Area */}
          {!uploadedFile ? <div onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onClick={() => fileInputRef.current?.click()} className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-all ${isDragOver ? 'border-[#E2231A] bg-[#FEE7E5]/50' : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'}`} data-id="element-4123" data-mp="2izc8k5839yeg">
              <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={e => {
            const file = e.target.files?.[0];
            if (file) handleFileSelect(file);
          }} data-id="element-4124" data-mp="75uhzgq04jg1" />

              <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 transition-colors ${isDragOver ? 'bg-[#E2231A]/10' : 'bg-gray-100'}`} data-id="element-4125" data-mp="3nxfyudu9x78t">
                <UploadIcon size={22} className={isDragOver ? 'text-[#9F140F]' : 'text-gray-500'} data-id="element-4126" data-mp="jjhfcraubmzq" />
              </div>
              <p className="text-sm font-medium text-gray-900 mb-1" data-id="element-4127" data-mp="aqe50rvhrq9b">
                {t('pages.BillForm.drag_drop_your_files_here')}
              </p>
              <p className="text-xs text-gray-500" data-id="element-4128" data-mp="179lh0t4gxco3">
                {t('pages.BillForm.supported_file_types_pdf_jpg_png')}
              </p>
            </div> /* Uploaded File Card */ : <div className="border border-gray-200 rounded-xl p-4" data-id="element-4129" data-mp="2l5mw196q1gb6">
              <div className="flex items-center gap-3" data-id="element-4130" data-mp="2q2daxt6si5v0">
                <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-4131" data-mp="2q4zrujfklxpw">
                  <FileTextIcon size={20} className="text-red-500" data-id="element-4132" data-mp="2z3tlq21io9zr" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-4133" data-mp="2z1gvz2piovc5">
                  <p className="text-sm font-medium text-gray-900 truncate" data-id="element-4134" data-mp="341v049nzzl4w">
                    {uploadedFile.name}
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-4135" data-mp="38zwona470cnr">
                    {formatFileSize(uploadedFile.size)}
                  </p>
                </div>
                {isExtracting ? <div className="flex items-center gap-2 text-[#9F140F]" data-id="element-4136" data-mp="3hwfci7xkhadh">
                    <LoaderIcon size={16} className="animate-spin" data-id="element-4137" data-mp="3muqjmawh96s0" />
                    <span className="text-xs font-medium" data-id="element-4138" data-mp="3rrz6upwhg104">
                      {t('pages.BillForm.extracting')}
                    </span>
                  </div> : extractionDone ? <div className="flex items-center gap-1.5 text-[#9F140F]" data-id="element-4139" data-mp="2xr1r83kcgsq4">
                    <CheckCircleIcon size={16} data-id="element-4140" data-mp="3bpropkb2suf0" />
                    <span className="text-xs font-medium" data-id="element-4141" data-mp="3bphx5saduf77">
                      {t('pages.BillForm.extracted')}
                    </span>
                  </div> : null}
                <button type="button" onClick={removeFile} aria-label="Remove file" className="p-1.5 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100 transition-colors" data-id="element-4142" data-mp="2jzwwnfvu2qmn">
                  <XIcon size={16} aria-hidden="true" data-id="element-4143" data-mp="2nzxe79e8bhxx" />
                </button>
              </div>

              {/* Extraction progress bar */}
              {isExtracting && <div className="mt-3 h-1 bg-gray-100 rounded-full overflow-hidden" data-id="element-4144" data-mp="1w99c9v4qe8j7">
                  <div className="h-full bg-gradient-to-r from-[#E2231A] to-[#EB4A42] rounded-full animate-pulse w-2/3" data-id="element-4145" data-mp="1w7ggxbzkjaqi" />
                </div>}
            </div>}

          {/* Extracted fields hint */}
          {extractionDone && <div className="bg-[#FEE7E5] border border-[#FBD4D1] rounded-lg px-4 py-3 flex items-start gap-2.5" data-id="element-4146" data-mp="1c8hjqanr1zkf">
              <CheckCircleIcon size={16} className="text-[#9F140F] mt-0.5 flex-shrink-0" data-id="element-4147" data-mp="1h6rdqhxqmu4g" />

              <p className="text-xs text-gray-700 leading-relaxed" data-id="element-4148" data-mp="29uwzn80j2p5v">
                {t('pages.BillForm.invoice_number_local_id_and_due')}
              </p>
            </div>}

          {/* Core Fields — always visible */}
          <div className="space-y-5" data-id="element-4149" data-mp="1zvy0cg1nk5vj">
            {/* Supplier — always required */}
            <div data-id="element-4150" data-mp="jhzizgai0aa6">
              <label className="block text-xs font-medium text-gray-700 mb-1.5" data-id="element-4151" data-mp="jhfybrtgr7sg">
                {t('pages.BillFinancingWizard.supplier')}{' '}
                <span className="text-red-400" data-id="element-4152" data-mp="of7s6rdo53za">*</span>
              </label>
              <SearchableSelect name="supplierName" options={supplierOptions} value={formData.supplierName} onChange={val => setFormData(prev => ({
              ...prev,
              supplierName: val
            }))} placeholder={t('pages.BillForm.attrs.select_supplier')} data-id="element-4153" data-mp="2y2nf581xj81r" />
            </div>

            {/* Amount + Currency side by side */}
            <div className="grid grid-cols-3 gap-3" data-id="element-4154" data-mp="2kcbkp2hvgx7g">
              <div className="col-span-2" data-id="element-4155" data-mp="25dzpl0a5dwie">
                <label className="block text-xs font-medium text-gray-700 mb-1.5" data-id="element-4156" data-mp="25f16hlduhd81">
                  {t('pages.BillForm.amount_due')}{' '}
                  <span className="text-red-400" data-id="element-4157" data-mp="3mcw8s2s9pir">*</span>
                </label>
                <input type="number" name="amountDue" step="0.01" min="0" value={formData.amountDue} onChange={handleChange} placeholder="0.00" className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" required data-id="element-4158" data-mp="3ka86dl6y6go" />
              </div>
              <div data-id="element-4159" data-mp="1y0ic2jpc7b68">
                <label className="block text-xs font-medium text-gray-700 mb-1.5" data-id="element-4160" data-mp="2btgfmtbxo9kv">
                  {t('pages.BillForm.currency')}
                </label>
                <SearchableSelect name="currency" options={CURRENCIES} value={formData.currency} onChange={val => setFormData(prev => ({
                ...prev,
                currency: val
              }))} placeholder={t('pages.BillApprovalWizard.usd')} data-id="element-4161" data-mp="27rv7tme9alhj" />
              </div>
            </div>

            {/* Due date */}
            <div data-id="element-4162" data-mp="2mivzpyh76ubz">
              <label className="block text-xs font-medium text-gray-700 mb-1.5" data-id="element-4163" data-mp="2mje4xklu8iw1">
                {t('pages.BillForm.due_date')}{' '}
                <span className="text-red-400" data-id="element-4164" data-mp="2vh7y4mob2wez">*</span>
              </label>
              <input type="date" name="dueDate" value={formData.dueDate} onChange={handleChange} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" required data-id="element-4165" data-mp="30gsxuzbav8sr" />
            </div>
          </div>

          {/* Auto-extracted fields — shown after extraction or always editable */}
          {(extractionDone || isEditMode) && <div className="border-t border-gray-100 pt-5 space-y-5" data-id="element-4166" data-mp="3o76k2977edff">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-4167" data-mp="3t58bpiip3l2i">
                {t('pages.BillForm.extracted_details')}
              </p>
              <div className="grid grid-cols-2 gap-3" data-id="element-4168" data-mp="75c2rhisndx6">
                <div data-id="element-4169" data-mp="24obqvukex2y">
                  <label className="block text-xs font-medium text-gray-700 mb-1.5" data-id="element-4170" data-mp="24683e5kpgl0">
                    {t('pages.BillForm.invoice_number')}
                  </label>
                  <input type="text" name="invoiceNumber" value={formData.invoiceNumber} onChange={handleChange} placeholder={t('pages.BillForm.attrs.autodetected')} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-4171" data-mp="3pa5h3cfw5equ" />
                </div>
                <div data-id="element-4172" data-mp="18ljgxili1ixs">
                  <label className="block text-xs font-medium text-gray-700 mb-1.5" data-id="element-4173" data-mp="18ltd5nx50mbx">
                    {t('pages.BillForm.local_id')}
                  </label>
                  <input type="text" name="localId" value={formData.localId} onChange={handleChange} placeholder={t('pages.BillForm.attrs.autodetected')} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-4174" data-mp="ocjp8mqjakxl" />
                </div>
              </div>
            </div>}

          {/* Actions */}
          <div className="pt-4 flex justify-end gap-3" data-id="element-4175" data-mp="1c3yvgqlflme4">
            <button type="button" onClick={() => navigate('/bills')} className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-4176" data-mp="y9enr5mt4lbv">
              {t('pages.BillApprovalWizard.cancel')}
            </button>
            <button type="submit" disabled={!formData.supplierName || !formData.amountDue} className="px-6 py-2.5 text-sm font-medium text-white bg-[#E2231A] rounded-lg hover:bg-[#C41B14] transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-4177" data-mp="1utnd5i09v6bj">
              {t('pages.BillForm.save')}
            </button>
          </div>
        </form>
      </div>
    </div>;
}